import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { LOGO_URL } from "../lib/assets";

const PRODUCTS = [
    {
        n: 1,
        image: "https://customer-assets.emergentagent.com/job_dentro-landing/artifacts/20zktnru_yogurte.jpg",
        name_pt: "Iogurte Grego Desnatado Zero Lactose",
        name_en: "Greek Yogurt — Skim, Zero Lactose",
        result_pt: "Boa escolha",
        result_en: "Good choice",
        color_label: "Verde · Green",
        tone: "green",
        note_pt: "Produto alinhado ao perfil saudável. Alto em proteína, 0% gordura, sem lactose.",
        note_en: "Aligned with a healthy profile. High in protein, 0% fat, lactose-free.",
    },
    {
        n: 2,
        image: "https://customer-assets.emergentagent.com/job_dentro-landing/artifacts/5mn4kwkr_choco.jpg",
        name_pt: "Chocolate ao Leite Nestlé Classic",
        name_en: "Nestlé Classic Milk Chocolate",
        result_pt: "Melhor evitar",
        result_en: "Better to avoid",
        color_label: "Vermelho · Red",
        tone: "red",
        note_pt: "Ultraprocessado. Alto em açúcar e gorduras saturadas, contém lactose.",
        note_en: "Ultra-processed. High in sugar and saturated fats, contains lactose.",
    },
    {
        n: 3,
        image: "https://customer-assets.emergentagent.com/job_dentro-landing/artifacts/p7jjalvj_pao.jpg",
        name_pt: "Pão Wickbold 100% Integral Tradicional",
        name_en: "Wickbold 100% Whole Wheat Bread — Traditional",
        result_pt: "Com moderação",
        result_en: "In moderation",
        color_label: "Amarelo · Yellow",
        tone: "yellow",
        note_pt: "Fonte de fibras, mas contém glúten e deve ser consumido com moderação.",
        note_en: "Source of fiber, but contains gluten and should be consumed in moderation.",
    },
];

const TONE = {
    green: {
        bg: "bg-[#16A34A]/8",
        border: "border-[#16A34A]/25",
        text: "text-[#16A34A]",
        solid: "#16A34A",
        icon: CheckCircle2,
        emoji: "🟢",
    },
    yellow: {
        bg: "bg-[#F59E0B]/8",
        border: "border-[#F59E0B]/30",
        text: "text-[#F59E0B]",
        solid: "#F59E0B",
        icon: AlertTriangle,
        emoji: "🟡",
    },
    red: {
        bg: "bg-[#DC2626]/8",
        border: "border-[#DC2626]/30",
        text: "text-[#DC2626]",
        solid: "#DC2626",
        icon: XCircle,
        emoji: "🔴",
    },
};

export default function AppleReview() {
    useEffect(() => {
        document.title = "Apple Review — Dentro | Test Products";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div
            className="min-h-screen bg-white text-[#0B1C3A]"
            data-testid="apple-review-root"
        >
            {/* Minimal header */}
            <header className="border-b border-slate-100">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <img
                            src={LOGO_URL}
                            alt="Dentro"
                            className="h-8 w-8 rounded-lg object-cover"
                        />
                        <span className="font-display text-lg font-extrabold text-[#0B1C3A]">
                            Dentro
                        </span>
                    </Link>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B1C3A]/70 hover:text-[#16A34A]"
                        data-testid="apple-review-back"
                    >
                        <ArrowLeft size={14} /> Home
                    </Link>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
                <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#16A34A]">
                    Apple App Review · Test Guide
                </span>
                <h1
                    data-testid="apple-review-title"
                    className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight"
                >
                    Test Products · Produtos para teste
                </h1>

                <div className="mt-6 space-y-3 text-[15px] text-[#475569] leading-relaxed max-w-3xl">
                    <p>
                        <strong className="text-[#0B1C3A]">EN —</strong> Below
                        are three sample products you can scan inside the
                        Dentro app during the review. Each product and its
                        expected color-coded result is listed. The expected
                        verdict is based on the default user profile (no
                        restrictions, goal: <em>Manter saúde / Maintain
                        health</em>).
                    </p>
                    <p>
                        <strong className="text-[#0B1C3A]">PT-BR —</strong>{" "}
                        Abaixo estão três produtos de exemplo que você pode
                        escanear dentro do app Dentro durante a avaliação.
                        Cada produto traz o resultado esperado por cor. O
                        veredito considera o perfil padrão de usuário (sem
                        restrições, meta: <em>Manter saúde</em>).
                    </p>
                </div>

                <div className="mt-12 grid gap-6">
                    {PRODUCTS.map((p) => {
                        const t = TONE[p.tone];
                        const Icon = t.icon;
                        return (
                            <article
                                key={p.n}
                                data-testid={`product-card-${p.n}`}
                                className={`rounded-3xl border ${t.border} ${t.bg} p-6 lg:p-8`}
                            >
                                <div className="grid md:grid-cols-[220px_1fr] gap-6 lg:gap-10 items-start">
                                    <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-center aspect-square">
                                        <img
                                            src={p.image}
                                            alt={p.name_en}
                                            className="max-h-full max-w-full object-contain"
                                            data-testid={`product-image-${p.n}`}
                                        />
                                    </div>

                                    <div>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-white border border-slate-200 text-[#0B1C3A] font-extrabold text-sm">
                                                {p.n}
                                            </span>
                                            <span
                                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${t.text} text-xs font-extrabold uppercase tracking-[0.18em] bg-white border ${t.border}`}
                                            >
                                                <Icon size={14} strokeWidth={2.6} />
                                                {t.emoji} {p.color_label}
                                            </span>
                                        </div>

                                        <h2
                                            className="mt-4 font-display text-2xl sm:text-3xl font-extrabold text-[#0B1C3A] leading-tight"
                                            data-testid={`product-name-en-${p.n}`}
                                        >
                                            {p.name_en}
                                        </h2>
                                        <p
                                            className="mt-1 text-base text-[#475569]"
                                            data-testid={`product-name-pt-${p.n}`}
                                        >
                                            {p.name_pt}
                                        </p>

                                        <div className="mt-5 grid sm:grid-cols-2 gap-3">
                                            <div className="rounded-2xl bg-white border border-slate-100 p-4">
                                                <div className="text-[11px] uppercase tracking-widest font-bold text-[#0B1C3A]/60">
                                                    Expected result · EN
                                                </div>
                                                <div
                                                    className="mt-1 font-display text-xl font-extrabold"
                                                    style={{ color: t.solid }}
                                                    data-testid={`product-result-en-${p.n}`}
                                                >
                                                    {p.result_en}
                                                </div>
                                                <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                                                    {p.note_en}
                                                </p>
                                            </div>
                                            <div className="rounded-2xl bg-white border border-slate-100 p-4">
                                                <div className="text-[11px] uppercase tracking-widest font-bold text-[#0B1C3A]/60">
                                                    Resultado esperado · PT-BR
                                                </div>
                                                <div
                                                    className="mt-1 font-display text-xl font-extrabold"
                                                    style={{ color: t.solid }}
                                                    data-testid={`product-result-pt-${p.n}`}
                                                >
                                                    {p.result_pt}
                                                </div>
                                                <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                                                    {p.note_pt}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>

                <div className="mt-12 rounded-3xl bg-[#F8FAFC] border border-slate-100 p-6 lg:p-8">
                    <h3 className="font-display text-lg font-bold text-[#0B1C3A]">
                        Notes for reviewers · Notas para avaliadores
                    </h3>
                    <ul className="mt-3 space-y-2 text-[14px] text-[#475569] list-disc pl-5">
                        <li>
                            <strong>EN:</strong> Results may vary if the test
                            user profile sets specific restrictions (e.g.,
                            lactose, gluten) or different goals (e.g., weight
                            loss, muscle gain).
                        </li>
                        <li>
                            <strong>PT-BR:</strong> Os resultados podem variar
                            se o perfil do usuário de teste incluir restrições
                            específicas (ex.: lactose, glúten) ou metas
                            diferentes (ex.: emagrecer, ganhar massa).
                        </li>
                        <li>
                            <strong>EN/PT:</strong> Dentro is educational and
                            does not replace medical or nutritional advice. ·
                            O Dentro é educativo e não substitui orientação
                            médica ou nutricional.
                        </li>
                    </ul>
                </div>

                <p className="mt-10 text-xs text-[#475569]">
                    © {new Date().getFullYear()} Dentro · apple-review ·{" "}
                    <a
                        href="mailto:contato@dentro.app.br"
                        className="text-[#16A34A] font-semibold hover:underline"
                    >
                        contato@dentro.app.br
                    </a>
                </p>
            </main>
        </div>
    );
}
